import * as React from "react"
import { To } from "react-router-dom"
import { useOnClickOutside } from "usehooks-ts"
import { HiMagnifyingGlass } from "react-icons/hi2"
import { Popover, Disclosure, Transition } from "@headlessui/react"
import { useFloating, useInteractions, useDismiss } from "@floating-ui/react"
import {
  FaChevronDown, FaChevronUp,
  FaXmark, FaBars,
  FaMagnifyingGlass
} from "react-icons/fa6"
import { Base } from "./Base"
import { Container } from "@allied/react-web/Container"
import { Link } from "@allied/react-web/Router"
import { Spinner } from "@allied/react-web/Spinner"

type HeaderProps = {
  children?: React.ReactNode
  logoImage?: string
  logoUrl?: To
  logoDescription?: React.ReactNode
  sublogoImage?: string
  sublogoUrl?: To
  sublogoDescription?: React.ReactNode
  menuItems?: HeadingItem[]
  sticky?: boolean
  searchLoading?: boolean
  searchShow?: boolean
  searchItems?: SearchItem[]
  suggestionItems?: string[]
  onSearch?: ({ keyword }: { keyword: string }) => void
  onSearchClose?: () => void
  onSelectItem?: (item: SearchItem) => void
}

type HeadingItem = {
  to: To
  target?: string
  content: React.ReactNode
  hideCrawl?: boolean
  items?: MenuItem[]
}

type MenuItem = {
  to: To
  target?: string
  content: React.ReactNode
  items?: MenuItem[]
  hideCrawl?: boolean
}

export function AlliedMarine(props: HeaderProps): React.JSX.Element {
  const {
    menuItems = [],
    searchLoading = false,
    searchShow = false,
    searchItems = [],
    suggestionItems = [],
    onSearch = () => { },
    onSearchClose = () => { },
    onSelectItem = () => { },
  } = props
  // const [overlay, setOverlay] = React.useState(true)

  const { refs, context, floatingStyles } = useFloating({
    placement: "bottom",
  })
  const dismiss = useDismiss(context)
  const { getReferenceProps, getFloatingProps } = useInteractions([
    dismiss,
  ])

  return (
    <Base ref={refs.setReference} props={getReferenceProps()} sticky={props.sticky} appendClassNames="font-verdana sm:font-poppins">
      <div className="w-full h-fit">
        <div className="flex xl:hidden">
          {/* {
            overlay &&
            <div className="fixed inset-0 bg-black opacity-30" />
          } */}
          <div className="bg-white w-full border-b-4 border-acsm-red py-4 z-10">
            <Container size="md">
              <div className="flex items-center justify-between">
                <div className="flex flex-row justify-between gap-2 divide-x-2 divide-black">
                  {
                    props.logoUrl ?
                      <Link to={props.logoUrl} className="no-underline text-sm px-2">
                        <img src={props.logoImage} alt="logo" className="w-7 h-12" />
                      </Link>
                      :
                      <div className="px-2">
                        <img src={props.logoImage} alt="logo" className="w-7 h-12" />
                      </div>
                  }

                  <div className="flex flex-row items-end pl-2">
                    {
                      props.sublogoUrl ?
                        <Link to={props.sublogoUrl} className="no-underline text-sm px-2">
                          <img src={props.sublogoImage} alt="sublogo" className="w-10 h-12" />
                        </Link>
                        :
                        <div className="px-2">
                          <img src={props.sublogoImage} alt="sublogo" className="w-10 h-12" />
                        </div>
                    }
                  </div>
                </div>

                <div className="flex flex-row justify-center items-center gap-2">
                  <Popover>
                    {({ open, close }) => (
                      <>
                        {/* {
                          React.useEffect(() => {
                            setOverlay(open)
                          }, [open])
                        } */}
                        <Popover.Button className="inline-flex items-center justify-center p-2 rounded-md"
                          onClick={() => {
                            onSearchClose && onSearchClose()
                          }}>
                          <span className="sr-only">open search</span>
                          {open ?
                            <FaXmark className="block h-6 w-6" aria-hidden="true" />
                            :
                            <FaMagnifyingGlass className="block h-6 w-6" aria-hidden="true" />
                          }
                        </Popover.Button>
                        <Popover.Panel className="w-full bg-white z-10 p-6" ref={refs.setFloating}
                          style={{ ...floatingStyles }} {...getFloatingProps()}>
                          <SearchBox
                            searchLoading={searchLoading}
                            searchShow={searchShow}
                            searchItems={searchItems}
                            suggestionItems={suggestionItems}
                            onSelectItem={(item) => {
                              onSelectItem && onSelectItem(item)
                              close()
                            }}
                            onSearch={onSearch} />
                        </Popover.Panel>
                      </>
                    )}
                  </Popover>

                  <Popover>
                    {({ open, close }) => (
                      <>
                        {/* {
                          React.useEffect(() => {
                            setOverlay(open)
                          }, [open])
                        } */}
                        <Popover.Button className="inline-flex items-center justify-center p-2 rounded-md">
                          <span className="sr-only">open menu</span>
                          {open ?
                            <FaXmark className="block h-6 w-6" aria-hidden="true" />
                            :
                            <FaBars className="block h-6 w-6" aria-hidden="true" />
                          }
                        </Popover.Button>
                        <Popover.Panel className="absolute top-[84px] right-0 w-5/6 h-screen bg-white z-10 p-6"
                          ref={refs.setFloating} {...getFloatingProps()}>
                          {
                            menuItems.length > 0 &&
                            <ul className="flex flex-col gap-4">
                              {
                                menuItems.map((mainMenu, i: number) => {
                                  return (
                                    <li key={`menu-item-mobile-${i}`}>
                                      {
                                        mainMenu.items && mainMenu.items.length > 0 ?
                                          <Disclosure>
                                            {({ open }) => (
                                              <div className="w-full flex flex-col gap-4">
                                                <Disclosure.Button className="flex flex-row justify-between items-center gap-2 hover:text-acsm-primary">
                                                  <span>{mainMenu.content}</span>
                                                  {open ?
                                                    <FaChevronUp className="block h-4 w-4" aria-hidden="true" />
                                                    :
                                                    <FaChevronDown className="block h-4 w-4" aria-hidden="true" />
                                                  }
                                                </Disclosure.Button>
                                                <Disclosure.Panel as="ul" className="flex flex-col gap-4 ml-8">
                                                  {
                                                    mainMenu.items &&
                                                    <div className="grid grid-cols-1 gap-2">
                                                      {
                                                        mainMenu.items.map((headingMenu, j: number) => {
                                                          return (
                                                            <div key={`heading-item-mobile-${i}-${j}`} className="flex flex-col gap-4">
                                                              {
                                                                headingMenu.to ?
                                                                  <Popover.Button as={Link} to={headingMenu.to}
                                                                    target={headingMenu.target} hideCrawl={headingMenu.hideCrawl}
                                                                    className="no-underline block text-acsm-red hover:text-acsm-primary">
                                                                    {headingMenu.content}
                                                                  </Popover.Button>
                                                                  :
                                                                  <span className="block text-acsm-red">
                                                                    {headingMenu.content}
                                                                  </span>
                                                              }
                                                              <hr className="border text-gray-500" />

                                                              {
                                                                headingMenu.items &&
                                                                <div className="flex flex-col gap-4 ml-8">
                                                                  {
                                                                    headingMenu.items.map((submenu, k: number) => {
                                                                      return (
                                                                        <Link key={`submenu-item-mobile-${i}-${j}-${k}`}
                                                                          to={submenu.to} target={submenu.target}
                                                                          hideCrawl={submenu.hideCrawl}
                                                                          className="hover:text-acsm-primary"
                                                                          onClick={() => { close() }}>
                                                                          {submenu.content}
                                                                        </Link>
                                                                      )
                                                                    })
                                                                  }
                                                                </div>
                                                              }
                                                            </div>
                                                          )
                                                        })
                                                      }
                                                    </div>
                                                  }
                                                </Disclosure.Panel>
                                              </div>
                                            )}
                                          </Disclosure>
                                          :
                                          <Link to={mainMenu.to} target={mainMenu.target} hideCrawl={mainMenu.hideCrawl}
                                            className="block no-underline hover:text-acsm-primary"
                                            onClick={() => { close() }}>
                                            {mainMenu.content}
                                          </Link>
                                      }
                                    </li>
                                  )
                                })
                              }
                            </ul>
                          }
                        </Popover.Panel>
                      </>
                    )}
                  </Popover>
                </div>
              </div>
            </Container>
          </div>
        </div>

        <div className="hidden xl:flex">
          <div className="bg-white w-full border-b-3 border-acsm-red">
            <Container size="md">
              <div className="flex items-center justify-between gap-2 w-full py-4">
                <div className="flex flex-row justify-between gap-2 xl:gap-6 divide-x-2 divide-black">
                  {
                    props.logoUrl ?
                      <Link to={props.logoUrl} className="flex flex-row items-center gap-2 px-2 no-underline text-sm">
                        <img src={props.logoImage} alt="logo" className="w-7 h-12" />
                        {
                          props.logoDescription &&
                          <span className="font-verdana text-xs text-[#231F20] hidden xl:block">{props.logoDescription}</span>
                        }
                      </Link>
                      :
                      <div className="flex flex-row items-center gap-2 px-2">
                        <img src={props.logoImage} alt="logo" className="w-7 h-12" />
                        {
                          props.logoDescription &&
                          <span className="font-verdana text-xs text-[#231F20] hidden xl:block">{props.logoDescription}</span>
                        }
                      </div>
                  }
                  {
                    props.sublogoUrl ?
                      <Link to={props.sublogoUrl} className="flex flex-row items-end gap-2 pl-4 pr-2 no-underline text-sm">
                        <img src={props.sublogoImage} alt="sublogo" className="w-10 h-12" />
                        {
                          props.sublogoDescription &&
                          <span className="text-xs hidden xl:block">{props.sublogoDescription}</span>
                        }
                      </Link>
                      :
                      <div className="flex flex-row items-end gap-2 pl-4 pr-2">
                        <img src={props.sublogoImage} alt="sublogo" className="w-10 h-12" />
                        {
                          props.sublogoDescription &&
                          <span className="text-xs hidden xl:block">{props.sublogoDescription}</span>
                        }
                      </div>
                  }
                </div>
                {
                  menuItems.length > 0 &&
                  <Popover.Group className="flex flex-row items-center justify-between gap-4 xl:gap-16 p-2">
                    {
                      menuItems.map((mainMenu, i: number) => {
                        return (
                          <Popover key={`menu-item-desktop-${i}`} className="text-lg">
                            {({ open, close }) => (
                              mainMenu.items && mainMenu.items.length > 0 ?
                                <>
                                  <Popover.Button className="flex flex-row justify-center items-center gap-2 hover:text-acsm-primary text-center">
                                    <span>{mainMenu.content}</span>
                                    {
                                      open ?
                                        <FaChevronUp className="block h-4 w-4" aria-hidden="true" />
                                        :
                                        <FaChevronDown className="block h-4 w-4" aria-hidden="true" />
                                    }
                                  </Popover.Button>

                                  <Transition as={React.Fragment}
                                    enter="transition duration-500 ease-out"
                                    enterFrom="transform scale-95 opacity-0"
                                    enterTo="transform scale-100 opacity-100"
                                    leave="transition duration-500 ease-out"
                                    leaveFrom="transform scale-100 opacity-100"
                                    leaveTo="transform scale-95 opacity-0">
                                    <Popover.Panel className="w-full !left-64 !top-2 bg-white z-10 p-7 rounded-md shadow-lg ring-1 ring-black ring-opacity-5"
                                      ref={refs.setFloating} style={{ ...floatingStyles, maxWidth: "800px" }} {...getFloatingProps()}>
                                      {
                                        mainMenu.items && mainMenu.items.length > 0 &&
                                        <div className="grid grid-cols-1 gap-2">
                                          {
                                            mainMenu.items.map((headingMenu, j: number) => {
                                              return (
                                                <div key={`heading-item-desktop-${i}-${j}`} className="flex flex-col gap-4">
                                                  {
                                                    headingMenu.to ?
                                                      <div>
                                                        <Popover.Button as={Link} to={headingMenu.to}
                                                          target={headingMenu.target} hideCrawl={headingMenu.hideCrawl}
                                                          className="no-underline text-lg text-acsm-red hover:text-acsm-primary">
                                                          {headingMenu.content}
                                                        </Popover.Button>
                                                      </div>
                                                      :
                                                      <span className="text-lg text-acsm-red">
                                                        {headingMenu.content}
                                                      </span>
                                                  }
                                                  <hr className="border text-gray-500" />

                                                  {
                                                    headingMenu.items &&
                                                    <div className="grid grid-cols-3 gap-2">
                                                      {
                                                        headingMenu.items.map((subMenu, k: number) => {
                                                          return (
                                                            <React.Fragment key={`submenu-item-desktop-${i}-${j}-${k}`}>
                                                              <div className="rounded-md py-4 px-2 hover:bg-acsm-primary cursor-pointer flex justify-center items-center text-center">
                                                                <Link to={subMenu.to} target={subMenu.target}
                                                                  hideCrawl={subMenu.hideCrawl} onClick={() => { close() }}>
                                                                  {subMenu.content}
                                                                </Link>
                                                              </div>
                                                            </React.Fragment>
                                                          )
                                                        })
                                                      }
                                                    </div>
                                                  }
                                                </div>
                                              )
                                            })
                                          }
                                        </div>
                                      }
                                    </Popover.Panel>
                                  </Transition>
                                </>
                                :
                                <Link to={mainMenu.to} target={mainMenu.target} hideCrawl={mainMenu.hideCrawl}
                                  className="flex flex-row gap-1 hover:text-acsm-primary"
                                  onClick={() => { close() }}>
                                  {mainMenu.content}
                                </Link>
                            )}
                          </Popover>
                        )
                      })
                    }
                  </Popover.Group>
                }
                <div>
                  <SearchBox
                    searchLoading={searchLoading}
                    searchShow={searchShow}
                    searchItems={searchItems}
                    suggestionItems={suggestionItems}
                    onSelectItem={(item) => {
                      onSelectItem && onSelectItem(item)
                    }}
                    onSearch={onSearch} />
                </div>
              </div>
            </Container>
          </div>
        </div>
      </div>
    </Base>
  )
}

type InputSearchProps = {
  id?: string
  name?: string
  style?: React.CSSProperties | undefined
  className?: string
  placeholder?: string
  defaultValue?: string | number | ReadonlyArray<string> | undefined
  value?: string | ReadonlyArray<string> | number | undefined
  required?: boolean
  disabled?: boolean
  readOnly?: boolean
  onInput?: (e: React.FormEvent<HTMLInputElement>) => void
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement, Element>) => void
  onSubmit?: () => void
  onInputClick?: (e: React.MouseEvent<HTMLInputElement>) => void
}

function InputSearch(props: InputSearchProps): React.JSX.Element {
  const {
    required = false,
    disabled = false,
    readOnly = false,
    className = "w-full p-2 border rounded-tl rounded-bl"
  } = props

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    props.onInputClick && props.onInputClick(e)
  }

  const handleInput = (e: React.FormEvent<HTMLInputElement>) => {
    props.onInput && props.onInput(e)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    props.onChange && props.onChange(e)
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    props.onBlur && props.onBlur(e)
  }

  const handleSubmit = () => {
    props.onSubmit && props.onSubmit()
  }

  return (
    <div className="flex flex-row">
      <input
        type={"search"}
        id={props.id}
        name={props.name}
        placeholder={props.placeholder}
        style={props.style}
        className={className}
        defaultValue={props.defaultValue}
        value={props.value}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        onInput={handleInput}
        onChange={handleChange}
        onBlur={handleBlur}
        onClick={handleInputClick}
      />

      <div className="p-3 bg-acsm-primary rounded-tr-3xl rounded-br-3xl cursor-pointer" onClick={handleSubmit}>
        <HiMagnifyingGlass className="w-6 h-6 text-black" />
      </div>
    </div>
  )
}

type SearchItem = {
  name: string
  url: string
  slug?: string
}

type SearchBoxProps = {
  searchLoading?: boolean
  searchShow?: boolean
  searchItems?: SearchItem[]
  suggestionItems?: string[]
  onSearch?: ({ keyword }: { keyword: string }) => void
  onSelectItem?: (item: SearchItem) => void
}

function SearchBox(props: SearchBoxProps): React.JSX.Element {
  const {
    searchLoading = false,
    searchShow = false,
    searchItems = [],
    suggestionItems = [],
    onSearch = () => { },
    onSelectItem = () => { },
  } = props

  const containerRef = React.useRef<HTMLDivElement>(null)
  const [keyword, setKeyword] = React.useState("")
  const [search, setSearch] = React.useState({
    show: searchShow,
    loading: searchLoading,
    items: searchItems
  })
  const [suggestion, setSuggestion] = React.useState({
    show: false,
    items: suggestionItems
  })
  const { refs, floatingStyles, context } = useFloating({
    placement: "bottom-start",
  })
  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([
    dismiss,
  ])

  function selectSuggestion(keyword: string) {
    setKeyword(keyword)

    onSearch && onSearch({
      keyword
    })
  }

  function selectSearch(item: SearchItem) {
    setSuggestion((prevState) => {
      return {
        ...prevState,
        show: false,
      }
    })
    setSearch((prevState) => {
      return {
        ...prevState,
        show: false,
      }
    })
    onSelectItem && onSelectItem(item)
  }

  // @note: 
  // don't call search callback here to avoid clearing the input trigger the search
  const handleInputClick = () => {
    if (keyword.length === 0) {
      setSuggestion((prevState) => {
        return {
          ...prevState,
          show: true
        }
      })
      return
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const keyword = e.currentTarget.value
    setKeyword(keyword)

    if (keyword.length === 0) {
      setSuggestion((prevState) => {
        return {
          ...prevState,
          show: true
        }
      })
      setSearch((prevState) => {
        return {
          ...prevState,
          show: false,
        }
      })
    }

    onSearch && onSearch({
      keyword
    })
  }

  const handleInputSubmit = () => {
    onSearch && onSearch({
      keyword
    })
  }

  useOnClickOutside(containerRef, (e) => {
    setSuggestion((prevState) => {
      return {
        ...prevState,
        show: false,
      }
    })
    setSearch((prevState) => {
      return {
        ...prevState,
        show: false,
      }
    })
  })

  React.useEffect(() => {
    setSuggestion((prevState) => {
      return {
        ...prevState,
        items: suggestionItems
      }
    })
  }, [suggestionItems])

  React.useEffect(() => {
    setSearch((prevState) => {
      return {
        ...prevState,
        show: searchShow,
        loading: searchLoading,
        items: searchItems
      }
    })
  }, [searchShow, searchLoading, searchItems])

  return (
    <div ref={containerRef}>
      <div className="w-full" ref={refs.setReference} {...getReferenceProps()}>
        <InputSearch
          className="min-w-[260px] w-full px-5 py-3 rounded-tl-3xl rounded-bl-3xl bg-acsm-primary placeholder:text-gray-800 text-black"
          placeholder="What are you looking for?"
          value={keyword}
          onChange={handleInputChange}
          onInputClick={handleInputClick}
          onSubmit={handleInputSubmit} />

        {
          (suggestion.show || search.show) &&
          <div className="!top-2 w-full bg-white rounded-md p-4 shadow-lg ring-1 ring-black ring-opacity-5"
            ref={refs.setFloating} style={{ ...floatingStyles, maxWidth: "300px" }} {...getFloatingProps()}>
            {
              (suggestion.show && !search.show) &&
              <>
                <p className="text-gray-500 text-xs font-semibold mb-1">
                  Most popular searches:
                </p>
                {
                  suggestion.items && suggestion.items.length > 0 &&
                  <ul className="flex flex-row gap-x-6 text-sm">
                    {
                      suggestion.items.map((suggestion, i: number) => {
                        return (
                          <React.Fragment key={`suggestion-item-${i}`}>
                            <li className="cursor-pointer"
                              onClick={() => { selectSuggestion(suggestion) }}>
                              {suggestion}
                            </li>
                          </React.Fragment>
                        )
                      })
                    }
                  </ul>
                }
              </>
            }

            {
              search.show &&
              <>
                {
                  search.loading &&
                  <div>
                    <Spinner size="xs" label="Loading..." labeled />
                  </div>
                }

                {
                  !search.loading &&
                  <>
                    {
                      search.items.length === 0 &&
                      <p className="text-gray-500 text-xs font-semibold mb-1">
                        There's no item found, try another keyword
                      </p>
                    }

                    {
                      search.items.length > 0 &&
                      <ul className="flex flex-col gap-x-6">
                        {
                          search.items.map((searchItem, i: number) => {
                            return (
                              <React.Fragment key={`search-item-${i}`}>
                                <li className="cursor-pointer">
                                  <Link to={searchItem.url}
                                    className="no-underline hover:text-acsm-primary"
                                    onClick={() => {
                                      selectSearch(searchItem)
                                    }}>
                                    {searchItem.name}
                                  </Link>
                                </li>
                              </React.Fragment>
                            )
                          })
                        }
                      </ul>
                    }
                  </>
                }
              </>
            }
          </div>
        }
      </div>
    </div>
  )
}